// src/audio_sequencer/AudioSequencer.ts
import { AudioProcessor } from "audio_utils";
var AudioSequencer = class {
  constructor(context) {
    this._soundsList = [];
    this._nodeCollections = [];
    this.isPlaying = false;
    this._context = context;
    if (this._context == null)
      throw "AudioContext not set";
    this.preEffectsMix = this._context.createGain();
    this.masterMix = this._context.createGain();
    let wetMix = this._context.createGain();
    wetMix.gain.value = 0.1;
    let dryMix = this._context.createGain();
    dryMix.gain.value = 1;
    this.preEffectsMix.connect(wetMix);
    this.preEffectsMix.connect(dryMix);
    let fxNode = context.createConvolver();
    fxNode.buffer = AudioProcessor.generateReverbImpulse(context);
    dryMix.connect(this.masterMix);
    wetMix.connect(fxNode);
    fxNode.connect(this.masterMix);
    this.masterMix.connect(this._context.destination);
  }
  removeSound(ss) {
    let idx = this._soundsList.indexOf(ss);
    if (idx !== -1) {
      this._soundsList.splice(idx, 1);
    }
  }
  addSound(ss) {
    this._soundsList.push(ss);
  }
  ///@param delay - must be >= 0. A period of silence at start of playback
  ///which can be used to account for a countoff
  ///@param startPosition - the position (in seconds) to begin playback -
  ///like a playhead position
  play(delay = 0, startPosition = 0) {
    if (this.isPlaying)
      return;
    this.isPlaying = true;
    let zeroTime = this._context.currentTime;
    startPosition -= delay;
    this._nodeCollections = this.createNodeCollections(
      this._soundsList,
      this._context,
      startPosition
    );
    for (let collection of this._nodeCollections) {
      let ss = collection.sequencerSound;
      let bsn = collection.bufferSourceNode;
      if (startPosition > ss.startTime) {
        bsn.start(zeroTime, startPosition - ss.startTime);
      } else {
        bsn.start(zeroTime + ss.startTime - startPosition);
      }
    }
    this.masterMix.gain.setValueAtTime(0, zeroTime);
    this.masterMix.gain.setValueAtTime(1, zeroTime + delay);
  }
  createNodeCollections(sounds, context, startPosition = 0, overrideOutput) {
    let collections = [];
    let zeroTime = context.currentTime;
    for (let sound of sounds) {
      let nodeCollection = new NodeCollection(sound);
      collections.push(nodeCollection);
      let bs = context.createBufferSource();
      bs.buffer = sound.audioBuffer;
      nodeCollection.addNode(bs);
      let mapGain = context.createGain();
      for (let time of sound.volumeMap.keys()) {
        let volume = sound.volumeMap.get(time);
        let volStartTime = zeroTime + sound.startTime + time - startPosition;
        if (volStartTime < zeroTime) {
          volStartTime = zeroTime;
        }
        mapGain.gain.setValueAtTime(volume, volStartTime);
      }
      nodeCollection.addNode(mapGain);
      let gainNode = context.createGain();
      gainNode.gain.value = sound.volume;
      nodeCollection.addNode(gainNode);
      nodeCollection.soundMasterGainNode = gainNode;
      let panNode = context.createStereoPanner();
      panNode.pan.value = sound.pan;
      nodeCollection.addNode(panNode);
      nodeCollection.soundMasterPanNode = panNode;
      nodeCollection.connectNodes(overrideOutput ?? this.preEffectsMix);
    }
    return collections;
  }
  async renderAudio() {
    let highestFrameEndTime = 0;
    for (let sound of this._soundsList) {
      let frameEndTime = sound.audioBuffer.length + 44100 * sound.startTime;
      if (frameEndTime > highestFrameEndTime) {
        highestFrameEndTime = frameEndTime;
      }
    }
    let context = new OfflineAudioContext(2, highestFrameEndTime, 44100);
    let startTime = context.currentTime;
    let collections = this.createNodeCollections(
      this._soundsList,
      context,
      0,
      context.destination
    );
    for (let collection of collections) {
      let bs = collection.bufferSourceNode;
      bs.start(startTime + collection.sequencerSound.startTime);
    }
    return new Promise((resolve, reject) => {
      context.addEventListener("complete", (e) => {
        if (e instanceof OfflineAudioCompletionEvent) {
          let buffer = e.renderedBuffer;
          resolve(buffer);
        } else {
          reject(e);
        }
      });
      context.startRendering();
    });
  }
  stop() {
    for (let n of this._nodeCollections) {
      n.bufferSourceNode.stop(0);
      n.removeAllNodes();
    }
    this.isPlaying = false;
  }
  getGainNodeForSequencerSound(ss) {
    if (this._nodeCollections == null)
      return null;
    let collection = this._nodeCollections.find((col) => col.sequencerSound === ss);
    return collection?.soundMasterGainNode;
  }
  getPanNodeForSequencerSound(ss) {
    let returnNode;
    this._nodeCollections.forEach((collection) => {
      if (ss === collection.sequencerSound) {
        collection.nodes.forEach((node) => {
          if (node instanceof StereoPannerNode) {
            returnNode = node;
          }
        });
      }
    });
    return returnNode;
  }
};
var NodeCollection = class {
  constructor(sequencerSound) {
    this.nodes = [];
    this.sequencerSound = sequencerSound;
  }
  addNode(node) {
    this.nodes.push(node);
    if (node instanceof AudioBufferSourceNode) {
      this.bufferSourceNode = node;
    }
  }
  connectNodes(outputNode) {
    for (let i = this.nodes.length - 1; i >= 0; i--) {
      this.nodes[i].connect(i < this.nodes.length - 1 ? this.nodes[i + 1] : outputNode);
    }
  }
  removeAllNodes() {
    for (let i = 0; i < this.nodes.length; i++) {
      this.nodes[i].disconnect();
    }
  }
};

// src/audio_sequencer/AudioElementSequencer.ts
import { AudioComponent } from "audio_output";

// src/audio_sequencer/SequencerSound.ts
import { createEmitter } from "ts_utils";
var SequencerSound = class _SequencerSound {
  // The pan property takes a unitless value between -1 (full left pan) and 1 
  // (full right pan). 
  constructor(audio, startTime, volume = 0.8, pan = 0) {
    this._delay = 0;
    /**volume in terms of seconds (key) to volume (value, 0.0-1.0)*/
    this._volumeMap = /* @__PURE__ */ new Map([[0, 1]]);
    this._emitter = createEmitter();
    if (audio instanceof AudioBuffer) {
      this._audioBuffer = audio;
    } else if (audio instanceof HTMLAudioElement) {
      this._audioElement = audio;
    }
    this._startTime = startTime;
    this._volume = volume;
    this._pan = pan;
  }
  get event() {
    return this._emitter.event;
  }
  static createSequencerSound(ab, startTime) {
    var ss = new _SequencerSound(ab, startTime);
    return ss;
  }
  get volume() {
    return this._volume;
  }
  set volume(volume) {
    this._volume = volume;
    this._emitter.emit(0 /* volumeChange */);
  }
  get pan() {
    return this._pan;
  }
  set pan(pan) {
    this._pan = pan;
    this._emitter.emit(1 /* panChange */);
  }
  get startTime() {
    return this._startTime;
  }
  set startTime(st) {
    this._startTime = st;
  }
  get delay() {
    return this._delay;
  }
  set delay(value) {
    this._delay = value;
    this._emitter.emit(2 /* delayChange */);
  }
  get audioBuffer() {
    return this._audioBuffer;
  }
  set audioBuffer(ab) {
    this._audioBuffer = ab;
  }
  get volumeMap() {
    return this._volumeMap;
  }
  set volumeMap(value) {
    this._volumeMap = value;
  }
  get audioElement() {
    return this._audioElement;
  }
  get audioElementSourceNode() {
    return this._audioElementSourceNode;
  }
  set audioElementSourceNode(value) {
    this._audioElementSourceNode = value;
  }
};
var SequencerSoundEvent = /* @__PURE__ */ ((SequencerSoundEvent2) => {
  SequencerSoundEvent2[SequencerSoundEvent2["volumeChange"] = 0] = "volumeChange";
  SequencerSoundEvent2[SequencerSoundEvent2["panChange"] = 1] = "panChange";
  SequencerSoundEvent2[SequencerSoundEvent2["delayChange"] = 2] = "delayChange";
  return SequencerSoundEvent2;
})(SequencerSoundEvent || {});

// src/audio_sequencer/AudioElementSequencer.ts
var AudioElementSequencer = class extends AudioComponent {
  constructor(context) {
    super();
    this._soundsList = [];
    this._nodeCollections = [];
    this._masterVolume = 1;
    this._isPlaying = false;
    this._checkForReadySounds = () => {
      let elapsedTime = this._context.currentTime - this._playStartTime;
      let playheadPosition = this._playheadStartPosition + elapsedTime;
      for (let i = this._unplayedSounds.length - 1; i >= 0; i--) {
        let sound = this._unplayedSounds[i];
        if (playheadPosition >= sound.startTime - 1) {
          this._nodeCollections.push(this._createNodeCollection(
            sound,
            this._context
          ));
          sound.audioElement.play();
          this._unplayedSounds.splice(i, 1);
        }
      }
    };
    this._onSoundPlaying = (e) => {
      let el = e.target;
      let nc = this._nodeCollections.find((n) => n.sequencerSound.audioElement === el);
      let elapsedTime = this._context.currentTime - this._playStartTime;
      let playheadPosition = this._playheadStartPosition + elapsedTime;
      nc.playOffset = nc.sequencerSound.startTime - playheadPosition;
      nc.updateDelay();
    };
    this._context = context;
    if (this._context == null)
      throw "AudioContext not set";
    this._masterMix = this._context.createGain();
  }
  removeSound(ss) {
    if (this._isPlaying) {
      throw "SequencerSound can not be removed during playback";
    }
    let idx = this._soundsList.indexOf(ss);
    if (idx !== -1) {
      this._soundsList.splice(idx, 1);
    }
  }
  addSound(ss) {
    if (ss.audioElementSourceNode == null) {
      ss.audioElementSourceNode = this._context.createMediaElementSource(ss.audioElement);
    }
    this._soundsList.push(ss);
  }
  /**prepares playback by seeking to appropriate time in audio elements and
   * choosing which sounds will play based on the [startPosition]. The [delay]
   * is in seconds and represents a silent period before playback reaches
   * the [startPosition]. It can be used to account for a countoff. The
   * [startPosition] is in seconds from the beginning of the song, which 
   * starts at 0.0. This is where audio will begin to be heard. For example, a 
   * [delay] of 2.0 seconds with a [startPosition] of 5.0 would be silent for 
   * 2 seconds and then start at the 5 second position of the song. If you had
   * a [SequencerSound] with a startPosition of 6.0, you'd get 2 seconds of
   * silence and then 1 more second of playback before the [SequencerSound]
   * begins at the 6.0 mark.*/
  async preparePlayback(delay = 0, startPosition = 0) {
    this._playheadStartPosition = startPosition - delay;
    this._delayDuration = delay;
    this._unplayedSounds = [];
    for (let sound of this._soundsList) {
      if (sound.startTime + sound.audioElement.duration > this._playheadStartPosition) {
        this._unplayedSounds.push(sound);
        sound.audioElement.addEventListener(
          "playing",
          this._onSoundPlaying
        );
      }
    }
    await this._seekToStartTimes(this._playheadStartPosition);
  }
  /**@param delay - must be >= 0. A period of silence at start of playback
   * which can be used to account for a countoff
   * @param startPosition - the position (in seconds) to begin playback -
   * like a playhead position*/
  play() {
    if (this._isPlaying)
      return;
    this._isPlaying = true;
    this._playStartTime = this._context.currentTime;
    this._masterMix.gain.setValueAtTime(0, this._playStartTime);
    this._masterMix.gain.setValueAtTime(
      this._masterVolume,
      this._playStartTime + this._delayDuration
    );
    this._checkForReadySounds();
    this._soundStartIntervalId = window.setInterval(
      this._checkForReadySounds,
      500
    );
  }
  stop() {
    if (!this._isPlaying) {
      return;
    }
    clearInterval(this._soundStartIntervalId);
    this._soundStartIntervalId = null;
    for (let n of this._nodeCollections) {
      n.cancelEventSub();
      n.sequencerSound.audioElement.pause();
      n.removeAllNodes();
    }
    this._nodeCollections = [];
    for (let s of this._soundsList) {
      s.audioElement.removeEventListener("playing", this._onSoundPlaying);
    }
    this._isPlaying = false;
  }
  _seekToStartTimes(startPosition) {
    return new Promise((resolve, reject) => {
      let quantityToSeek = 0;
      let quantitySeeked = 0;
      let onAudioElSeeked = (e) => {
        let el = e.target;
        el.removeEventListener("seeked", onAudioElSeeked);
        el.pause();
        quantitySeeked++;
        if (quantitySeeked === quantityToSeek) {
          resolve();
        }
      };
      for (let sound of this._soundsList) {
        let seekTime = startPosition > sound.startTime ? startPosition - sound.startTime : 0;
        if (sound.audioElement.currentTime !== seekTime) {
          quantityToSeek++;
          sound.audioElement.addEventListener(
            "seeked",
            onAudioElSeeked
          );
          sound.audioElement.currentTime = seekTime;
        }
      }
      if (quantityToSeek === 0) {
        resolve();
      }
    });
  }
  _createNodeCollection(sound, context) {
    let zeroTime = this._playStartTime;
    let nodeCollection = new AudioElementNodeCollection(sound);
    nodeCollection.addNode(sound.audioElementSourceNode);
    nodeCollection.delayNode = context.createDelay(2);
    nodeCollection.addNode(nodeCollection.delayNode);
    let mapGain = context.createGain();
    for (let time of sound.volumeMap.keys()) {
      let volume = sound.volumeMap.get(time);
      let volStartTime = zeroTime + sound.startTime + time - this._playheadStartPosition;
      if (volStartTime < zeroTime) {
        volStartTime = zeroTime;
      }
      mapGain.gain.setValueAtTime(volume, volStartTime);
    }
    nodeCollection.addNode(mapGain);
    let gainNode = context.createGain();
    gainNode.gain.value = sound.volume;
    nodeCollection.addNode(gainNode);
    nodeCollection.soundMasterGainNode = gainNode;
    let panNode = context.createStereoPanner();
    panNode.pan.value = sound.pan;
    nodeCollection.addNode(panNode);
    nodeCollection.soundMasterPanNode = panNode;
    nodeCollection.connectNodes(this._masterMix);
    return nodeCollection;
  }
  get outputNode() {
    return this._masterMix;
  }
  /**the masterVolume for all sounds, from 0.0 to 1.0*/
  get masterVolume() {
    return this._masterVolume;
  }
  set masterVolume(value) {
    this._masterVolume = value;
    if (this._isPlaying) {
      this._masterMix.gain.cancelScheduledValues(0);
      let playPosition = this._context.currentTime - this._playStartTime;
      if (playPosition >= this._delayDuration) {
        this._masterMix.gain.setValueAtTime(value, 0);
      } else {
        this._masterMix.gain.setValueAtTime(value, this._delayDuration - playPosition);
      }
    }
  }
};
var AudioElementNodeCollection = class {
  constructor(sequencerSound) {
    this.nodes = [];
    this._onSoundEvent = (e) => {
      if (e === 0 /* volumeChange */) {
        this.soundMasterGainNode.gain.value = this.sequencerSound.volume;
      } else if (e === 1 /* panChange */) {
        this.soundMasterPanNode.pan.value = this.sequencerSound.pan;
      } else if (e === 2 /* delayChange */) {
        this.updateDelay();
      }
    };
    this.sequencerSound = sequencerSound;
    sequencerSound.event.on(this._onSoundEvent);
  }
  addNode(node) {
    this.nodes.push(node);
    if (node instanceof MediaElementAudioSourceNode) {
      this.elementSourceNode = node;
    }
  }
  connectNodes(outputNode) {
    for (let i = this.nodes.length - 1; i >= 0; i--) {
      this.nodes[i].connect(i < this.nodes.length - 1 ? this.nodes[i + 1] : outputNode);
    }
  }
  removeAllNodes() {
    for (let i = 0; i < this.nodes.length; i++) {
      this.nodes[i].disconnect();
    }
  }
  updateDelay() {
    this.delayNode.delayTime.value = this.sequencerSound.delay + this.playOffset;
  }
  cancelEventSub() {
    this.sequencerSound.event.off(this._onSoundEvent);
  }
};

// src/audio_sequencer/looping/AudioLooper.ts
import { AudioComponent as AudioComponent2 } from "audio_output";
import { createEmitter as createEmitter3 } from "ts_utils";
import { BrowserUtils } from "ts_utils";

// src/audio_sequencer/looping/LoopTrack.ts
import { createEmitter as createEmitter2 } from "ts_utils";
var LoopTrack = class {
  constructor(audioBuffer, startTime, volume = 0.8, pan = 0) {
    this._emitter = createEmitter2();
    this._audioBuffer = audioBuffer;
    this._startTime = startTime;
    this._volume = volume;
    this._pan = pan;
    this._endTime = this._audioBuffer.duration;
  }
  get audioBuffer() {
    return this._audioBuffer;
  }
  set audioBuffer(value) {
    this._audioBuffer = value;
    this._emitter.emit(
      new LoopTrackEvent(1 /* audioBufferChanged */, this)
    );
  }
  get trackEvent() {
    return this._emitter.event;
  }
  /**takes the index of an entrance and returns true if the following entrance
   * happens immediately after it with no gap, based on the [playDuration].*/
  isNextEntranceConsecutive(currentEntranceIndex, masterLoopDuration) {
    let gap;
    if (currentEntranceIndex < this._entrances.length - 1) {
      gap = this._entrances[currentEntranceIndex + 1].time - this._entrances[currentEntranceIndex].time;
    } else {
      gap = masterLoopDuration - this._entrances[currentEntranceIndex].time + this._entrances[0].time;
    }
    return gap <= this.playDuration;
  }
  /**value from 0 to 1.0*/
  get volume() {
    return this._volume;
  }
  set volume(volume) {
    this._volume = volume;
    this._emitter.emit(
      new LoopTrackEvent(0 /* updated */, this)
    );
  }
  /**value from -1.0 (left) to 1.0 (right)*/
  get pan() {
    return this._pan;
  }
  set pan(pan) {
    this._pan = pan;
    this._emitter.emit(
      new LoopTrackEvent(0 /* updated */, this)
    );
  }
  /**The performance start position, in seconds, adjusted to account for the
   * countoff duration - recordDelay. Recorded audio typically includes
   * countoff samples at the start, so this value becomes the seek position in 
   * the audio buffer to skip past that period. See the notes in the 
   * [AudioLooper] class.*/
  get startTime() {
    return this._startTime;
  }
  set startTime(value) {
    this._startTime = value;
    this._emitter.emit(
      new LoopTrackEvent(0 /* updated */, this)
    );
  }
  /**The performance end position, in seconds. By default this matches the
   * [duration] property pulled from [audioBuffer.duration], but it can be
   * set to a shorter value to account for any extra time at the end of the
   * [audioBuffer] that should not be played.*/
  get endTime() {
    return this._endTime;
  }
  set endTime(value) {
    this._endTime = value <= this._audioBuffer.duration ? value : this._audioBuffer.duration;
    this._emitter.emit(
      new LoopTrackEvent(0 /* updated */, this)
    );
  }
  /**Shifts the track's playback in time by this many seconds. This is used to
   * account for mic latency, which we leave out of the [startTime]
   * calculation to avoid clipping the beginning of a recording when we seek.
   * The AudioLooper applies a DelayNode to each track. To leave room for the
   * DelayNode to adjust the track's time forward or back, the track is
   * started early by a standard amount, X. The DelayNode is then given X as
   * its default initial value so that the track sounds at the same playhead
   * time it would have without the early start. This [timeShift] value is
   * then subtracted from the DelayNode's value.
   * 
   * Example: A track inserted at the beginning of a song with a timeShift
   * value of -0.14. The [AudioLooper] starts the track at -0.5 seconds 
   * instead of 0 and sets its DelayNode to (0.5 - 0.14 = 0.36)*/
  get timeShift() {
    return this._timeShift;
  }
  set timeShift(value) {
    this._timeShift = value;
    this._emitter.emit(
      new LoopTrackEvent(0 /* updated */, this)
    );
  }
  /**the complete duration of the audio in seconds*/
  get duration() {
    return this._audioBuffer.duration;
  }
  /**the duration of the section of the audio that's intended to be played,
   * from the startTime to the endTime*/
  get playDuration() {
    return this._endTime - this._startTime;
  }
  /**a list of the play time stamps, in seconds, within the song that this 
   * track should start playing. 0 is the beginning of the song.*/
  get entrances() {
    return this._entrances;
  }
  set entrances(value) {
    this._entrances = value;
  }
};
var LoopTrackEntrance = class {
  constructor(time, enabled, info) {
    this.time = time;
    this.enabled = enabled;
    this.info = info;
  }
};
var LoopTrackEvent = class {
  constructor(type, track) {
    this.type = type;
    this.track = track;
  }
};
var LoopTrackEventType = /* @__PURE__ */ ((LoopTrackEventType2) => {
  LoopTrackEventType2[LoopTrackEventType2["updated"] = 0] = "updated";
  LoopTrackEventType2[LoopTrackEventType2["audioBufferChanged"] = 1] = "audioBufferChanged";
  return LoopTrackEventType2;
})(LoopTrackEventType || {});

// src/audio_sequencer/looping/AudioLooper.ts
var AudioLooper = class extends AudioComponent2 {
  /**/
  ////////Notes on start time vs. delays///////////*/
  /**The start time of each track is being set to match the approximate time
   * within the track that the countoff completed during its recording. So
   * essentially we skip this first part of each track. However, we use a
   * delay node for each track to delay it the amount needed for the current
   * countoff sent to the sequencer. So for example, if a recording was made
   * with a 2 second countoff, we set the start time on its bufferSournceNode
   * to 2 seconds. If we then call play in the sequencer with a 1 second
   * countoff, the track is given a 1 second delay via a delay node. */
  constructor(context) {
    super();
    this._trackList = [];
    this._nodeCollections = [];
    this._isPlaying = false;
    /**The time, in seconds, between the start of the sequencer's playback and
     * the start of the SoundLibPlayer's playback*/
    this._soundLibPlayerOffset = 0;
    /**The duration in seconds of the countoff period that occurs in playback
     * before audio elements should sound. This is set when play is called.*/
    this._countoffDuration = 0;
    /**loop process interval, in milliseconds*/
    this._loopTimerInterval = 500;
    this.defaultTrackDelay = 0.5;
    this._emitter = createEmitter3();
    this._onLoopTimer = () => {
      let ctxTime = this._context.currentTime;
      let playingTime = ctxTime - this._playStartTime;
      let playheadTime = this._playheadStartTime - this._countoffDuration + playingTime;
      let loopIteration = Math.floor(playheadTime / this._masterLoopDuration);
      let loopTime = playheadTime - this._masterLoopDuration * loopIteration;
      let window2 = this.lookAheadWindowSize;
      for (let track of this._trackList) {
        if (this._nodeCollections.some((nc) => nc.track === track && nc.playheadTime >= playheadTime)) {
          continue;
        }
        let nextEntrance = track.entrances.find((e) => e.time > loopTime);
        let nextEntranceTime;
        if (nextEntrance == null) {
          nextEntrance = track.entrances[0];
          nextEntranceTime = this._masterLoopDuration + nextEntrance.time;
        } else {
          nextEntranceTime = nextEntrance.time;
        }
        if (!nextEntrance.enabled) {
          continue;
        }
        if (loopTime + window2 >= nextEntranceTime - this.defaultTrackDelay) {
          let ncPlayheadTime = nextEntranceTime + loopIteration * this._masterLoopDuration;
          let tnc = this._createNodeCollection(track, ncPlayheadTime);
          tnc.playheadEndTime = ncPlayheadTime + track.playDuration;
          tnc.connectNodes(this._masterMix);
          this._updateTrackDelay(tnc);
          this._nodeCollections.push(tnc);
          let bsnPlayTime = ncPlayheadTime + this._playStartTime - this.defaultTrackDelay;
          tnc.bufferNode.start(bsnPlayTime, tnc.track.startTime);
          let ev = new AudioLooperEvent();
          ev.type = 0 /* entranceScheduled */;
          ev.track = track;
          ev.entrance = nextEntrance;
          ev.startTime = bsnPlayTime + tnc.delay.delayTime.value;
          ev.isEntranceBeginning = true;
          this._emitter.emit(ev);
        }
      }
      for (let i = this._nodeCollections.length - 1; i >= 0; i--) {
        let tnc = this._nodeCollections[i];
        if (playheadTime > tnc.playheadTime + tnc.track.playDuration) {
          tnc.bufferNode.stop(0);
          tnc.disconnectNodes();
          tnc.replaceDelayNode(this._context);
          this._nodeCollections.splice(i, 1);
        }
      }
    };
    this._onTrackEvent = (e) => {
      switch (e.type) {
        case 0 /* updated */: {
          this._handleLoopTrackUpdated(e.track);
          break;
        }
        case 1 /* audioBufferChanged */: {
          this._handleLooopTrackAudioBufferChanged(e.track);
          break;
        }
      }
    };
    this._context = context;
    if (this._context == null)
      throw "AudioContext not set";
    this._init();
  }
  get event() {
    return this._emitter.event;
  }
  _init() {
    LoopTrackNodeCollection.defaultTrackDelay = this.defaultTrackDelay;
    this._setupAudioGraph();
  }
  addTrack(track) {
    this._trackList.push(track);
    track.trackEvent.on(this._onTrackEvent);
    if (this._isPlaying) {
      this._initTrackPlayback([track]);
      this._onLoopTimer();
    }
  }
  removeTrack(track) {
    this._stopTrackPlayback(track);
    let idx = this._trackList.indexOf(track);
    if (idx !== -1) {
      this._trackList.splice(idx, 1);
      track.trackEvent.off(this._onTrackEvent);
    }
  }
  removeAllTracks() {
    for (let i = this._trackList.length - 1; i >= 0; i--) {
      this.removeTrack(this._trackList[i]);
    }
  }
  updateSoundLibPlayerOffset(offset) {
    this._soundLibPlayerOffset = offset;
    for (let nc of this._nodeCollections) {
      this._updateTrackDelay(nc);
    }
  }
  /**Starts playback. The [countoffDuration] should be set to the number of
   * seconds before audio elements should start playing. The [loopDuration] 
   * is the duration of the "master" loop which all looping tracks fit within.
   * The [playheadTime] is the 0 based position in the song/sequence that 
   * playback should start from. It does not include the countoff. Another
   * way to adjust the song start position is to set the 
   * [overridePlayStartTime]. The looper uses this value to calculate how
   * long playback has been running to determine the current position in the
   * loop. One strategy is to sync this with another sequencer.*/
  play(countoffDuration, loopDuration, {
    playheadTime = 0,
    overridePlayStartTime = null
  }) {
    if (this._isPlaying)
      return;
    this._isPlaying = true;
    this._countoffDuration = countoffDuration;
    this._masterLoopDuration = loopDuration;
    this._soundLibPlayerOffset = 0;
    this._playStartTime = overridePlayStartTime ?? this._context.currentTime;
    this._playheadStartTime = playheadTime;
    let volume = this.masterVolume;
    let ctxTime = this._context.currentTime;
    this._masterMix.gain.setValueAtTime(0, ctxTime);
    this._masterMix.gain.setValueAtTime(volume, ctxTime + countoffDuration);
    this._loopIntervalId = window.setInterval(
      this._onLoopTimer,
      this._loopTimerInterval
    );
    this._initTrackPlayback(this._trackList);
    this._onLoopTimer();
  }
  stop() {
    clearInterval(this._loopIntervalId);
    this._loopIntervalId = null;
    for (let track of this._trackList) {
      this._stopTrackPlayback(track);
    }
    this._isPlaying = false;
  }
  /**creates and returns a list of the currently playing [LoopTrack] 
   * instances. */
  getPlayingTracks() {
    let playheadTime = this.playheadTime;
    let playingTracks = [];
    this._nodeCollections.forEach((tnc) => {
      if (tnc.isInPlayingWindow(playheadTime)) {
        playingTracks.push(tnc.track);
      }
    });
    return playingTracks;
  }
  _stopTrackPlayback(track) {
    for (let i = this._nodeCollections.length - 1; i >= 0; i--) {
      let tnc = this._nodeCollections[i];
      if (tnc.track === track) {
        tnc.bufferNode.stop(0);
        tnc.disconnectNodes();
        tnc.replaceDelayNode(this._context);
        this._nodeCollections.splice(i, 1);
      }
    }
  }
  //start playback at playheadTime = passed in playheadTime - countoffDuration
  //and set the master volume to 0 for the countoffDuration
  _initTrackPlayback(tracks) {
    let ctxTime = this._context.currentTime;
    let playingTime = ctxTime - this._playStartTime;
    let playheadTime = this._playheadStartTime - this._countoffDuration + playingTime;
    let insertPlayheadTime = playheadTime + this.defaultTrackDelay;
    if (playheadTime < 0) {
      return;
    }
    let loopIteration = Math.floor(insertPlayheadTime / this._masterLoopDuration);
    let loopInsertTime = insertPlayheadTime - this._masterLoopDuration * loopIteration;
    for (let track of tracks) {
      let entrance;
      for (let i = track.entrances.length - 1; i >= 0; i--) {
        if (track.entrances[i].time <= loopInsertTime) {
          entrance = track.entrances[i];
          break;
        }
      }
      if (entrance == null || !entrance.enabled) {
        continue;
      }
      if (entrance.time + track.playDuration < loopInsertTime) {
        continue;
      }
      let elapsed = loopInsertTime - entrance.time;
      let tnc = this._createNodeCollection(track, playheadTime);
      tnc.connectNodes(this._masterMix);
      this._updateTrackDelay(tnc);
      this._nodeCollections.push(tnc);
      tnc.entranceOffset = elapsed;
      tnc.playheadEndTime = playheadTime + track.playDuration - elapsed;
      tnc.bufferNode.start(ctxTime, tnc.track.startTime + elapsed);
      let ev = new AudioLooperEvent();
      ev.type = 0 /* entranceScheduled */;
      ev.track = track;
      ev.entrance = entrance;
      ev.startTime = ctxTime + tnc.delay.delayTime.value;
      ev.isEntranceBeginning = elapsed <= 0;
      this._emitter.emit(ev);
    }
  }
  _createNodeCollection(track, playheadTime) {
    let tnc = new LoopTrackNodeCollection(track);
    tnc.playheadTime = playheadTime;
    tnc.bufferNode = this._context.createBufferSource();
    tnc.bufferNode.buffer = tnc.track.audioBuffer;
    tnc.delay = this._context.createDelay(20);
    this._updateTrackDelay(tnc);
    tnc.masterGain = this._context.createGain();
    tnc.masterGain.gain.setValueAtTime(track.volume, 0);
    if (!BrowserUtils.isSafari()) {
      tnc.masterPan = this._context.createStereoPanner();
      tnc.masterPan.pan.setValueAtTime(track.pan, 0);
    }
    return tnc;
  }
  _updateTrackDelay(nodeCollection) {
    let delay = this.defaultTrackDelay + nodeCollection.track.timeShift + this._soundLibPlayerOffset;
    if (delay < 0)
      delay = 0;
    if (nodeCollection.delay.delayTime.value !== delay) {
      nodeCollection.delay.delayTime.value = delay;
    }
  }
  _handleLoopTrackUpdated(track) {
    let tncList = this._nodeCollections.filter((t) => t.track === track);
    for (let tnc of tncList) {
      if (tnc.masterGain.gain.value !== track.volume) {
        tnc.masterGain.gain.setValueAtTime(track.volume, 0);
      }
      if (tnc.masterPan != null && tnc.masterPan.pan.value !== track.pan) {
        tnc.masterPan.pan.setValueAtTime(track.pan, 0);
      }
      this._updateTrackDelay(tnc);
    }
  }
  _handleLooopTrackAudioBufferChanged(track) {
    if (!this._isPlaying) {
      return;
    }
    for (let i = this._nodeCollections.length - 1; i >= 0; i--) {
      let tnc = this._nodeCollections[i];
      if (tnc.track === track) {
        let playheadTime = this.playheadTime;
        if (tnc.isInPlayingWindow(playheadTime)) {
          let track2 = tnc.track;
          let elapsed = playheadTime - tnc.entrancePlayheadTime;
          let playheadEndTime = tnc.playheadEndTime;
          this._stopTrackPlayback(tnc.track);
          let tnc2 = this._createNodeCollection(track2, playheadTime);
          tnc2.entranceOffset = elapsed;
          tnc2.playheadEndTime = playheadEndTime;
          tnc2.connectNodes(this._masterMix);
          this._updateTrackDelay(tnc2);
          this._nodeCollections.splice(i, 0, tnc2);
          tnc2.bufferNode.start(
            this._context.currentTime,
            tnc2.track.startTime + elapsed + this.defaultTrackDelay
          );
        }
      }
    }
  }
  _setupAudioGraph() {
    this._masterMix = this._context.createGain();
    this._masterMix.gain.value = 0.8;
  }
  /**true if the AudioLooper is currently processing playback, even if no
   * tracks are added.*/
  get isPlaying() {
    return this._isPlaying;
  }
  /**the AudioContext.currentTime value considered as the start point of
   * playback. The loop position is calculated from this. It can be overridden
   * in the [play] method.*/
  get playStartTime() {
    return this._playStartTime;
  }
  /**The [playheadTime] is the current 0 based position in the song/sequence.
   * It does not include the countoff. */
  get playheadTime() {
    let playingTime = this._context.currentTime - this._playStartTime;
    return this._playheadStartTime + playingTime - this._countoffDuration;
  }
  /**gets the duration of the overall loop. Individual tracks can loop more
   * frequently within this. The value is set when calling [play].*/
  get masterLoopDuration() {
    return this._masterLoopDuration;
  }
  /**gets the time in seconds if the playback position within the current
   * loop iteration. Resets to 0 at start of each iteration.*/
  get loopTime() {
    let playingTime = this._context.currentTime - this._playStartTime;
    let playheadTime = this._playheadStartTime - this._countoffDuration + playingTime;
    let loopIteration = Math.floor(playheadTime / this._masterLoopDuration);
    let loopTime = playheadTime - this._masterLoopDuration * loopIteration;
    return loopTime;
  }
  /**Iteration index of the master loop, with 0 as the first iteration. Loop 
   * time resets to 0 with each iteration of master loop*/
  get loopIteration() {
    let playingTime = this._context.currentTime - this._playStartTime;
    let playheadTime = this._playheadStartTime - this._countoffDuration + playingTime;
    return Math.floor(playheadTime / this._masterLoopDuration);
  }
  get masterVolume() {
    return this._masterMix.gain.value;
  }
  set masterVolume(value) {
    if (value < 0)
      value = 0;
    else if (value > 1)
      value = 1;
    this._masterMix.gain.setValueAtTime(value, 0);
  }
  /**during playback, a timer is used to load tracks to play at their
   * entrances, and any entrances within this window of time are scheduled. If
   * you want to adjust whether entrances play or not, you should do so before
   * entrances come within this window of time. Note that the window is
   * augmented by the [defaultTrackDelay], so the total window is larger. Ex.
   * 1 second window + 0.5 second defaultTrackDelay = 1.5 seconds.*/
  get lookAheadWindowSize() {
    return 2 * this._loopTimerInterval / 1e3;
  }
  get outputNode() {
    return this._masterMix;
  }
};
var LoopTrackNodeCollection = class {
  constructor(track) {
    /**If an entrance is started after the natural beginning time of its
     * occurrence within the master loop, an entrance offset (EO) is passed to
     * bufferNode.play(ctxTime, track.startTime + EO) so that playback starts
     * further into the audio buffer. This is in addition to the track.startTime
     * that already is used to mark the beginning of the desired audio in the
     * LoopTrack. We can use this to calculate when the node collection will
     * finish. */
    this.entranceOffset = 0;
    this.track = track;
  }
  /**the master loop playhead time when the entrance associated with this
   * node collection started. The node collection itself may have started
   * playing later than this if it was created for an entrance that was
   * already past its starting point (see playheadTime)*/
  get entrancePlayheadTime() {
    return this.playheadTime - this.entranceOffset;
  }
  connectNodes(outputNode) {
    this.bufferNode.connect(this.delay);
    this.delay.connect(this.masterGain);
    if (this.masterPan != null) {
      this.masterGain.connect(this.masterPan);
      this.masterPan.connect(outputNode);
    } else {
      this.masterGain.connect(outputNode);
    }
  }
  disconnectNodes() {
    this.masterPan?.disconnect();
    this.masterGain.disconnect();
    this.delay.disconnect();
    this.bufferNode.disconnect();
  }
  /**replacing the delay node prevents buffered audio from playing when
   * restarting playback.*/
  replaceDelayNode(context) {
    let newDelay = context.createDelay(20);
    newDelay.delayTime.value = this.delay.delayTime.value;
    this.delay = newDelay;
  }
  /**calculates and returns the total time audio for this node collection
   * should play, adjusting for the track.startTime and entranceOffset. */
  getPlayDuration() {
    return this.track.playDuration - this.entranceOffset;
  }
  /**returns true if the current playheadTime of the master loop includes the
   * time span of this entrance (playheadTime to playheadEndTime), meaning
   * it should be playing.*/
  isInPlayingWindow(currentPlayheadTime) {
    return this.playheadTime <= currentPlayheadTime && this.playheadEndTime > currentPlayheadTime;
  }
};
var AudioLooperEvent = class {
};
var AudioLooperEventType = /* @__PURE__ */ ((AudioLooperEventType2) => {
  AudioLooperEventType2[AudioLooperEventType2["entranceScheduled"] = 0] = "entranceScheduled";
  return AudioLooperEventType2;
})(AudioLooperEventType || {});
export {
  AudioElementSequencer,
  AudioLooper,
  AudioLooperEvent,
  AudioLooperEventType,
  AudioSequencer,
  LoopTrack,
  LoopTrackEntrance,
  LoopTrackEvent,
  LoopTrackEventType,
  SequencerSound,
  SequencerSoundEvent
};
